import { RefreshResponseSchema } from '@saniplus/contracts';

import { isApiError } from './apiError';
import { apiFetch } from './http';

/**
 * What a refresh attempt actually established.
 *
 * A refused session and an unreachable server both leave the caller without a
 * token, and for a long time both were reported as the same bare `null` —
 * which is how a worker who reopened the installed PWA offline got signed out.
 * Only a refusal is the session ending; an unreachable server is a fact about
 * the plant's signal and about nothing else.
 */
export interface RefreshOutcome {
  /** The rotated access token, or `null` when none could be obtained. */
  readonly token: string | null;
  /** `true` when the request never reached the server (offline, DNS, socket). */
  readonly offline: boolean;
}

const REFUSED: RefreshOutcome = { token: null, offline: false };
const UNREACHABLE: RefreshOutcome = { token: null, offline: true };

/**
 * The single in-flight refresh promise.
 *
 * Ten requests can fail with 401 in the same tick (a dashboard fans out six
 * queries at once). Each one calling `POST /auth/refresh` would rotate the
 * token ten times, and reuse detection (D-05) would read the second rotation
 * as a stolen token and revoke the whole family — logging the user out for
 * behaving normally. So the first caller starts the refresh and everyone else
 * awaits the same promise.
 */
let inFlight: Promise<RefreshOutcome> | null = null;

/**
 * How long a *refused* refresh is remembered before another caller is allowed
 * to retry the network. Without this, staggered 401s (several queries expiring
 * within the same tick but landing on different ticks) each call
 * `refreshAccessToken()` after the in-flight promise has already settled
 * without a token — every one of them re-hits `/auth/refresh` against a
 * session that is already known to be dead. A refusal is the only thing that
 * knowledge can come from, so it is the only thing that arms this.
 */
const REFRESH_FAILURE_COOLDOWN_MS = 5_000;
let failedUntil: number | null = null;

async function performRefresh(): Promise<RefreshOutcome> {
  try {
    const result = await apiFetch({ url: '/auth/refresh', method: 'POST', body: {} });
    const token = RefreshResponseSchema.parse(result.data).accessToken;
    failedUntil = null;
    return { token, offline: false };
  } catch (error) {
    /*
     * The cooldown's whole content is "this session is already known to be
     * dead", and an unreachable server has not said that about anything. Arming
     * it here would make the caller that runs the moment signal comes back —
     * the sync flush, or the retry behind a 401 — answer `null` with no request
     * at all, which is a five-second window in which a perfectly good session
     * looks expired. `status: 0` is `networkError`'s mark (`apiError.ts`); a
     * body that fails `RefreshResponseSchema` is a broken server, not a broken
     * link, and stays a refusal.
     */
    if (isApiError(error) && error.status === 0) return UNREACHABLE;
    failedUntil = Date.now() + REFRESH_FAILURE_COOLDOWN_MS;
    return REFUSED;
  }
}

/**
 * Resolves with a fresh access token, or with the reason there is none — a
 * refusal (the session is gone) or an unreachable server (nothing is known).
 * Within the cooldown after a *refusal*, resolves immediately without a new
 * network call.
 */
export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (inFlight) return inFlight;
  if (failedUntil !== null && Date.now() < failedUntil) return Promise.resolve(REFUSED);
  const pending = performRefresh().finally(() => {
    if (inFlight === pending) inFlight = null;
  });
  inFlight = pending;
  return pending;
}

/**
 * Forget a previous failure's cooldown, because a NEW session now exists.
 *
 * The cooldown remembers "the refresh route said no" for five seconds so a
 * fan-out of staggered 401s does not re-ask a session that is already known to
 * be dead. But a login (or a forced PIN change) in the meantime makes that
 * memory a lie: the cookie in the jar is not the one that was refused. Left
 * standing, the very first 401 after signing in resolves to `null` **without a
 * network call**, `baseQuery` reads that as an unrecoverable session and
 * dispatches `sessionExpired` — and a brand-new hire who has just chosen her
 * PIN is bounced to `/login` holding a perfectly good token.
 *
 * Every cold start hits this: the boot `bootstrapSession` refresh 401s because
 * there is no cookie yet, which arms the cooldown seconds before the login
 * that makes it wrong.
 *
 * Only the cooldown is cleared. An in-flight refresh is left alone: it is a
 * promise other callers are already awaiting, and dropping it would let a
 * second `/auth/refresh` rotate the family behind the first one's back.
 */
export function clearRefreshFailure(): void {
  failedUntil = null;
}

/** Test-only: forget any promise or cooldown left over from a previous case. */
export function resetRefreshState(): void {
  inFlight = null;
  failedUntil = null;
}
